import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { ReactComponent as ForPersonIcon } from '../../../../../assets/icons/sharing/forPerson.svg';
import { ReactComponent as FromPersonIcon } from '../../../../../assets/icons/sharing/fromPerson.svg';
import { ReactComponent as SharedByIcon } from '../../../../../assets/icons/sharing/sharedBy.svg';
import CopyActionButton from '../../../../../components/designe/ActionButtons/CopyActionButton';
import DeleteActionButton from '../../../../../components/designe/ActionButtons/DeleteActionButton';
import EditActionButton from '../../../../../components/designe/ActionButtons/EditActionButton';
import ShareActionButton from '../../../../../components/designe/ActionButtons/ShareActionButton';
import AppDialog from '../../../../../components/reusable/app-dialog/AppDialog';
import ConfirmDialog from '../../../../../components/reusable/confirm-dialog/ConfirmDialog';
import { useNavigationStack } from '../../../../../context/NavigationStackContext';
import {
  useCopyProgramItem,
  useDeleteProgramItems,
} from '../../../../../redux/api/programsAPI';
import { useGetMyDetails } from '../../../../../redux/api/users/usersApi';
import { Paths } from '../../../../../shared/paths';
import { IProgramItem } from '../../../../../types/interfaces/program-item';
import CopyProgramItem from '../../copy-program-item/CopyProgramItem';
import { useStyles } from './ProgramItemListItem.styles';
import {
  ProgramListKind,
  getProgramListSubtitle,
} from './program-list-subtitle';
import ShareProgram from './share-program/ShareProgram';

interface IProgramItemListItemProps {
  programItem: IProgramItem;
  listKind?: ProgramListKind;
}

export default function ProgramItemListItem(props: IProgramItemListItemProps) {
  const { programItem, listKind = 'my' } = props;
  const { navigate } = useNavigationStack();
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [showCopyProgramItemDialog, setShowCopyProgramItemDialog] =
    useState(false);
  const { t } = useTranslation();
  const [deleteProgramItem] = useDeleteProgramItems();
  const [copyProgramItem] = useCopyProgramItem();
  const theme = useTheme();
  const { data: user } = useGetMyDetails();

  const { classes } = useStyles(theme);
  const [isShareProgram, setIsShareProgram] = useState(false);

  const listSubtitle = useMemo(
    () => getProgramListSubtitle(programItem, user, listKind),
    [programItem, user, listKind],
  );

  const SubtitleIcon =
    listSubtitle?.icon === 'forPerson'
      ? ForPersonIcon
      : listSubtitle?.icon === 'sharedBy'
        ? SharedByIcon
        : FromPersonIcon;

  function itemClickHandler() {
    navigate(Paths.programs.toViewItemId(programItem.id));
  }

  function onCopyProgramItem(title: string, programCollectionId?: string) {
    return copyProgramItem({
      itemId: programItem.id,
      title,
      programCollectionId,
    })
      .unwrap()
      .then((data) => {
        navigate(Paths.programs.toViewItemId(data.id));
      })
      .catch((error) => {
        console.log(error);
      });
  }

  return (
    <div
      className={classes.programItemWrapper}
      onClick={() => itemClickHandler()}>
      <div className={classes.programItemHeader}>
        <Box className={classes.programItemTextColumn}>
          <Typography variant="h3">{programItem.title}</Typography>
          {listSubtitle && (
            <Box
              display="flex"
              alignItems="center"
              gap={0.5}
              sx={{ color: 'primary.main' }}
              marginBottom={0.5}>
              <SubtitleIcon
                style={{
                  width: 12,
                  height: 12,
                  flexShrink: 0,
                }}
              />
              <Typography variant="text-sm" color="primary" component="span">
                {listSubtitle.icon === 'forPerson'
                  ? t('trainings.listSubtitleForUser', {
                      name: listSubtitle.label,
                    })
                  : t('trainings.listSubtitleFromUser', {
                      name: listSubtitle.label,
                    })}
              </Typography>
            </Box>
          )}
          {programItem?.programCollection && (
            <Typography
              variant={'success-wrapped-sm'}
              className={classes.programCollectionLine}>
              {programItem.programCollection.title}
            </Typography>
          )}
        </Box>

        <Box
          className={classes.buttonsPanel}
          onClick={(e) => e.stopPropagation()}>
          {programItem?.permissions?.canCopy && (
            <CopyActionButton
              onClick={(e) => setShowCopyProgramItemDialog(true)}
            />
          )}

          {programItem?.permissions?.canShare && (
            <ShareActionButton onClick={(e) => setIsShareProgram(true)} />
          )}

          {programItem?.permissions?.canEdit && (
            <EditActionButton
              onClick={(e) =>
                navigate(Paths.programs.toEditItemId(programItem.id))
              }
            />
          )}

          {programItem?.permissions?.canDelete && (
            <DeleteActionButton onClick={(e) => setDeleteConfirm(true)} />
          )}
        </Box>
      </div>

      <div onClick={(e) => e.stopPropagation()}>
        <ConfirmDialog
          open={!!deleteConfirm}
          onClose={() => setDeleteConfirm(null)}
          title={t('common.delete')}
          onSubmit={() => {
            setDeleteConfirm(null);
            deleteProgramItem({
              id: programItem.id,
            });
          }}>
          {t('common.deleteQuestion')}
        </ConfirmDialog>

        <AppDialog
          open={showCopyProgramItemDialog}
          onClose={() => setShowCopyProgramItemDialog(false)}
          title={t('programs.copyProgramToCollection')}>
          <CopyProgramItem
            programItem={programItem}
            onCopyProgramItem={onCopyProgramItem}
            onCancel={() => setShowCopyProgramItemDialog(false)}
          />
        </AppDialog>

        <ShareProgram
          open={!!isShareProgram}
          programItem={programItem}
          onClose={() => setIsShareProgram(false)}
        />
      </div>
    </div>
  );
}
